import { ReactNode, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ModalProps {
  children: ReactNode;
  show: boolean;
  onClose?: () => void;
  className?: string;
  ariaLabelledBy?: string;
}

const Modal = ({
  children,
  show,
  onClose,
  className: maxSize,
  ariaLabelledBy,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  // Close on Escape key
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, handleClose]);

  // Focus management: save previous focus, restore on close
  useEffect(() => {
    if (show) {
      previouslyFocusedElement.current =
        document.activeElement as HTMLElement | null;

      // Focus the modal container or the close button after render
      requestAnimationFrame(() => {
        const closeButton =
          modalRef.current?.querySelector<HTMLElement>("[data-modal-close]");
        if (closeButton) {
          closeButton.focus();
        } else {
          modalRef.current?.focus();
        }
      });
    } else {
      // Restore focus to previously focused element
      previouslyFocusedElement.current?.focus();
      previouslyFocusedElement.current = null;
    }
  }, [show]);

  // Trap focus within the modal
  useEffect(() => {
    if (!show) return;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift+Tab: if on first element, wrap to last
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last element, wrap to first
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTabKey);
    return () => document.removeEventListener("keydown", handleTabKey);
  }, [show]);

  if (!show) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
      aria-labelledby={ariaLabelledBy}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className={`relative animate-[modal-enter_200ms_ease-out] ${maxSize || ""}`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {onClose && (
          <button
            data-modal-close
            type="button"
            onClick={handleClose}
            className="focus-visible:ring-soluud-primary absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors duration-150 hover:bg-gray-200 hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            aria-label="Close dialog"
          >
            <X size={16} />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
